import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, Avatar, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  LinearProgress, Divider, List, ListItem, ListItemAvatar, ListItemText, CircularProgress
} from '@mui/material';
import {
  People as PeopleIcon,
  School as SchoolIcon,
  CheckCircle as CheckIcon,
  Assignment as AssignmentIcon,
  Schedule as ScheduleIcon,
  MenuBook as MenuBookIcon,
  Event as EventIcon,
  CalendarMonth as CalendarIcon,
  AccessTime as ClockIcon,
  TrendingUp as TrendingUpIcon,
  Announcement as AnnouncementIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const TeacherDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTeacherData();
  }, []);

  const fetchTeacherData = async () => {
    try {
      // Try to load real data
      const res = await api.get('/dashboard/stats');
      setStats(res.data.data);
    } catch (e) {
      // Fallback
    } finally {
      setLoading(false);
    }
  };

  const quickActions = [
    { label: 'Mark Attendance', icon: <CheckIcon />, path: '/attendance', color: '#4caf50' },
    { label: 'Enter Marks', icon: <AssignmentIcon />, path: '/exams/marks-entry', color: '#ff9800' },
    { label: 'View Students', icon: <PeopleIcon />, path: '/students', color: '#1a9e8f' },
    { label: 'Timetable', icon: <ScheduleIcon />, path: '/timetable/settings', color: '#9c27b0' },
    { label: 'Library', icon: <MenuBookIcon />, path: '/library', color: '#2196f3' },
    { label: 'Results', icon: <SchoolIcon />, path: '/exams/student-report', color: '#e53935' },
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', flexDirection: 'column', gap: 2 }}>
        <CircularProgress size={48} sx={{ color: '#1a9e8f' }} />
        <Typography variant="body1" color="text.secondary">Loading dashboard...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Welcome Header */}
      <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #1a9e8f 0%, #158a7d 100%)', color: '#fff' }}>
        <CardContent sx={{ py: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h5" fontWeight={700}>
                Welcome back, {user?.firstName || 'Teacher'}!
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.5 }}>
                {user?.userRole?.name || 'Teacher'} • VP School MIS
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Chip
                icon={<CalendarIcon sx={{ fontSize: 14, color: '#fff !important' }} />}
                label={new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                size="small"
                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 600 }}
              />
              <Chip
                icon={<ClockIcon sx={{ fontSize: 14, color: '#fff !important' }} />}
                label={new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                size="small"
                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 600 }}
              />
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>Quick Actions</Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {quickActions.map(action => (
          <Grid item xs={4} sm={2} key={action.label}>
            <Card
              onClick={() => navigate(action.path)}
              sx={{
                cursor: 'pointer', textAlign: 'center', py: 2,
                transition: 'all 0.2s',
                '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }
              }}
            >
              <Avatar sx={{ bgcolor: `${action.color}15`, color: action.color, width: 48, height: 48, mx: 'auto', mb: 1 }}>
                {action.icon}
              </Avatar>
              <Typography variant="caption" fontWeight={600} sx={{ fontSize: 11 }}>
                {action.label}
              </Typography>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Teacher-specific Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase' }}>
                    My Students
                  </Typography>
                  <Typography variant="h4" fontWeight={700} sx={{ my: 0.5 }}>
                    {stats?.totalStudents || '--'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Across all assigned classes</Typography>
                </Box>
                <Avatar sx={{ bgcolor: '#1a9e8f15', color: '#1a9e8f', width: 48, height: 48 }}>
                  <PeopleIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase' }}>
                    Today's Attendance
                  </Typography>
                  <Typography variant="h4" fontWeight={700} sx={{ my: 0.5 }}>
                    {stats?.attendanceRate ? `${stats.attendanceRate}%` : '--'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Mark attendance if not done</Typography>
                </Box>
                <Avatar sx={{ bgcolor: '#4caf5015', color: '#4caf50', width: 48, height: 48 }}>
                  <CheckIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase' }}>
                    Pending Exams
                  </Typography>
                  <Typography variant="h4" fontWeight={700} sx={{ my: 0.5 }}>
                    {stats?.pendingExams || '--'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Marks entry required</Typography>
                </Box>
                <Avatar sx={{ bgcolor: '#ff980015', color: '#ff9800', width: 48, height: 48 }}>
                  <AssignmentIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Notices + Schedule */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                <AnnouncementIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'middle' }} />
                Recent Notices
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemAvatar sx={{ minWidth: 36 }}>
                    <Avatar sx={{ width: 28, height: 28, bgcolor: '#e3f2fd', color: '#1976d2', fontSize: 12 }}>📢</Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={<Typography variant="body2" fontWeight={600}>Staff Meeting - Friday</Typography>}
                    secondary={<Typography variant="caption" color="text.secondary">All teachers must attend the meeting at 3:00 PM</Typography>}
                  />
                </ListItem>
                <Divider component="li" />
                <ListItem>
                  <ListItemAvatar sx={{ minWidth: 36 }}>
                    <Avatar sx={{ width: 28, height: 28, bgcolor: '#fff3e0', color: '#ef6c00', fontSize: 12 }}>📋</Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={<Typography variant="body2" fontWeight={600}>Exam Schedule Released</Typography>}
                    secondary={<Typography variant="caption" color="text.secondary">Unit Test 2 schedule has been published</Typography>}
                  />
                </ListItem>
                <Divider component="li" />
                <ListItem>
                  <ListItemAvatar sx={{ minWidth: 36 }}>
                    <Avatar sx={{ width: 28, height: 28, bgcolor: '#e8f5e9', color: '#388e3c', fontSize: 12 }}>🏫</Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={<Typography variant="body2" fontWeight={600}>Annual Day Preparation</Typography>}
                    secondary={<Typography variant="caption" color="text.secondary">Submit class performances list by next week</Typography>}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                <EventIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'middle' }} />
                Today's Schedule
              </Typography>
              <List dense>
                {[
                  { time: '8:00 AM', subject: 'Mathematics', class: 'Class 5A', color: '#1a9e8f' },
                  { time: '9:00 AM', subject: 'Science', class: 'Class 6B', color: '#2196f3' },
                  { time: '10:30 AM', subject: 'Mathematics', class: 'Class 4A', color: '#1a9e8f' },
                  { time: '11:30 AM', subject: 'Science', class: 'Class 5B', color: '#2196f3' },
                  { time: '1:00 PM', subject: 'Mathematics', class: 'Class 3C', color: '#1a9e8f' },
                ].map((slot, i) => (
                  <React.Fragment key={i}>
                    <ListItem>
                      <Chip label={slot.time} size="small" sx={{ mr: 1.5, fontWeight: 600, fontSize: 10, minWidth: 72 }} />
                      <ListItemText
                        primary={<Typography variant="body2" fontWeight={600}>{slot.subject}</Typography>}
                        secondary={<Typography variant="caption" color="text.secondary">{slot.class}</Typography>}
                      />
                      <Chip label="Upcoming" size="small" variant="outlined" sx={{ fontSize: 10 }} />
                    </ListItem>
                    {i < 4 && <Divider component="li" />}
                  </React.Fragment>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TeacherDashboard;
